/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/label-has-associated-control */

"use client"

import clsx from "clsx"
import Image from "next/image"
import Link from "next/link"
import { useState, useEffect, useRef, useCallback } from "react"

import {
  ListRegionCollection,
  PayoutRegionCountry,
} from "@/queries/payoutCountries"

import Arrow from "../../../../public/icons/arrow-up.svg"
import Check from "../../../../public/icons/check.svg"
import Plus from "../../../../public/icons/plus-icon.svg"

interface CountrySelectorProps {
  entry: ListRegionCollection
}

export default function CountrySelector(props: CountrySelectorProps) {
  const { entry } = props
  const { items: regions = [] } = entry

  const allCountries = regions.flatMap(
    (region) => region.countryListCollection.items,
  )
  const defaultCountry =
    allCountries.find((c) => c.countryName === "United States") ||
    allCountries[0]

  const [selectedCountry, setSelectedCountry] =
    useState<PayoutRegionCountry>(defaultCountry)
  const [query, setQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Filtered countries based on search input
  const filteredCountries = useCallback(
    () =>
      query === ""
        ? allCountries
        : allCountries.filter((country) =>
            country.countryName.toLowerCase().includes(query.toLowerCase()),
          ),
    [allCountries, query],
  )

  // Handle clicking outside the dropdown to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) return

    if (event.key === "ArrowDown") {
      setHighlightedIndex((prev) => (prev + 1) % filteredCountries().length)
    } else if (event.key === "ArrowUp") {
      setHighlightedIndex(
        (prev) =>
          (prev - 1 + filteredCountries().length) % filteredCountries().length,
      )
    } else if (event.key === "Enter") {
      // Prevent Enter if no valid results exist
      if (filteredCountries().length === 0) return

      const selected = filteredCountries()[highlightedIndex]
      if (!selected) return // Prevent selecting an invalid option

      setSelectedCountry(selected)
      setIsOpen(false)
      setQuery(selected.countryName)
    } else if (event.key === "Escape") {
      setIsOpen(false)
    }
  }

  return (
    <>
      <div className="col-span-2 lg:col-span-4">
        {/* Searchable Dropdown */}
        <h2>
          <label
            id="countrySelectLabel"
            className="block heading2 text-gray-90 mb-2"
          >
            Receiving Country
          </label>
        </h2>
        <div className="relative mb-4" ref={dropdownRef}>
          {/* Input Field for Search */}
          <input
            type="text"
            className="w-full p-3 border border-gray-15 rounded-md bg-white body3"
            placeholder="Select country"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setIsOpen(true)
              setHighlightedIndex(0)
            }}
            onClick={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="absolute inset-y-0 right-2 flex items-center"
            onClick={() => {
              setQuery("") // Clears the input field
              setIsOpen(!isOpen) // Toggles dropdown open/close
            }}
            type="button"
            id="open-close-country-list"
            aria-expanded={isOpen}
          >
            {query.length > 0 ? ( // Show Plus only if there is text
              <Plus className="transition duration-200 ease-in-out h-5 w-5 mr-2 rotate-45 stroke-gray-70" />
            ) : (
              <Arrow
                className={clsx(
                  "transition duration-200 ease-in-out h-5 w-5 mr-2 rotate-180",
                  isOpen && "rotate-0",
                )}
              />
            )}
          </button>

          {/* Dropdown Options */}
          {isOpen && (
            <div className="absolute w-full bg-gray-0 border rounded-md shadow-md mt-1 z-10 max-h-60 overflow-y-auto border-gray-15">
              {filteredCountries().length === 0 ? (
                <div className="p-2 text-gray-500">No results found</div>
              ) : (
                regions
                  .filter(
                    (region) =>
                      query === "" ||
                      region.countryListCollection.items.some((c) =>
                        c.countryName
                          .toLowerCase()
                          .includes(query.toLowerCase()),
                      ),
                  )
                  .sort((a, b) => a.region.localeCompare(b.region)) // 🔥 Sort regions alphabetically
                  .map((region) => (
                    <div key={region.region}>
                      <div className="px-3 py-2 caption3 bold text-gray-90 bg-gray-5">
                        {region.region}
                      </div>
                      <ul
                        className="p-2"
                        role="listbox"
                        aria-labelledby="countrySelectLabel"
                      >
                        {filteredCountries()
                          .filter((c) =>
                            region.countryListCollection.items.includes(c),
                          )
                          .map((country, index) => {
                            const globalIndex = allCountries.findIndex(
                              (c) => c.countryName === country.countryName,
                            )

                            const statusStyles: Record<string, string> = {
                              // eslint-disable-next-line quote-props
                              "New": "text-newblue-50 bg-blue-5",
                              "Coming Soon": "text-green-50 bg-green-5",
                            } as const

                            return (
                              <li
                                key={country.countryName}
                                role="option"
                                aria-selected={highlightedIndex === index}
                                tabIndex={0}
                                onClick={() => {
                                  setSelectedCountry(country)
                                  setIsOpen(false)
                                  setQuery(country.countryName)
                                }}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    setSelectedCountry(country)
                                    setIsOpen(false)
                                    setQuery(country.countryName)
                                  }
                                }}
                                className={clsx(
                                  "flex items-center gap-2 p-2 cursor-pointer w-full text-left text-gray-50 hover:bg-gray-2.5 country-select-option",
                                  highlightedIndex === globalIndex ||
                                    selectedCountry.countryName ===
                                      country.countryName
                                    ? "text-gray-90"
                                    : "",
                                  highlightedIndex === globalIndex
                                    ? "bg-gray-2.5"
                                    : "",
                                )}
                              >
                                <Image
                                  src={country.countryFlag.url}
                                  alt={country.countryFlag.title}
                                  width={20}
                                  height={14}
                                  className="rounded"
                                />
                                {country.countryName}
                                {statusStyles[country.status] && (
                                  <span
                                    className={`ml-2 text-xs font-medium px-2 py-0.5 rounded ${statusStyles[country.status]}`}
                                  >
                                    {country.status}
                                  </span>
                                )}
                                {selectedCountry.countryName ===
                                  country.countryName && (
                                  <Check className="ml-auto h-5 w-5" />
                                )}
                              </li>
                            )
                          })}
                      </ul>
                    </div>
                  ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="col-span-2 lg:col-span-8">
        {/* Country Info Card */}
        {selectedCountry && (
          <div className="p-4 lg:p-8 bg-newblue-5 rounded-lg shadow-lg">
            <div className="flex items-center gap-4 mb-4 lg:mb-6">
              <Image
                src={selectedCountry.countryFlag.url}
                alt={selectedCountry.countryFlag.title}
                width={40}
                height={26}
                className="rounded shadow-md"
              />
              <h2 className="caption2 font-semibold text-gray-90">
                PAYOUT CURRENCY: {selectedCountry.payoutCurrency.join(", ")}
              </h2>
            </div>
            <table className="shadow-sm w-full table md:table-fixed">
              <tbody>
                <tr>
                  <td className="caption2">Use Cases (Payment Type)</td>
                  <td className="body3">
                    {selectedCountry.useCases.join(", ")}
                  </td>
                </tr>
                <tr>
                  <td className="caption2">Payout Channels</td>
                  <td className="body3">
                    {selectedCountry.payoutChannels.join(", ")}
                  </td>
                </tr>
                <tr>
                  <td className="caption2">Max Transaction</td>
                  <td className="body3">
                    {selectedCountry.maxTransactionSize}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <p className="body2 mt-6">
          Don’t see your desired payout country listed? We’ve got you covered.{" "}
          <Link href="/contact/sales/" className="text-newblue-50">
            Contact our sales
          </Link>{" "}
          team to explore additional payout options tailored to your needs.
        </p>
      </div>
    </>
  )
}
